import classNames from "classnames";
import { Link } from "react-router-dom";
import { useRequests } from "../../api/developmentRequests";
import { useInventoryItems } from "../../api/inventory";
import { useMarkNotificationRead, useNotifications } from "../../api/notifications";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Page } from "../../components/ui/Page";
import PageLoader from "../../components/ui/PageLoader";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLayoutTitle } from "../../hooks/useLayoutTitle";
import { useTranslation } from "../../hooks/useTranslation";
import { formatUserName } from "../../types/user";
import styles from "./DashboardPage.module.css";

function statLink(base: string, params: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) query.set(key, value);
  }
  const qs = query.toString();
  return qs ? `${base}?${qs}` : base;
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  useLayoutTitle(`${t("dashboard.welcome")}, ${user.firstName}`);

  const isAdmin = user?.isAdmin === true;

  const { data: allRequestsPage, isLoading: allReqLoading } = useRequests();
  const { data: myRequestsPage, isLoading: myReqLoading } = useRequests({ mine: true });
  const { data: allInventoryPage, isLoading: allInvLoading } = useInventoryItems();
  const { data: myInventoryPage, isLoading: myInvLoading } = useInventoryItems({ mine: true });
  const isLoading = allReqLoading || myReqLoading || allInvLoading || myInvLoading;
  const { data: notifications = [] } = useNotifications();
  const markRead = useMarkNotificationRead();

  const allRequests = allRequestsPage?.data ?? [];
  const myRequests = myRequestsPage?.data ?? [];
  const allInventoryItems = allInventoryPage?.data ?? [];
  const myInventoryItems = myInventoryPage?.data ?? [];

  const myReqByStatus = {
    new: myRequests.filter((r) => r.status === "new").length,
    in_progress: myRequests.filter((r) => r.status === "in_progress").length,
    completed: myRequests.filter((r) => r.status === "completed").length,
  };

  const myInventoryByStatus = {
    under_review: myInventoryItems.filter((i) => i.status === "under_review").length,
    taken_over: myInventoryItems.filter((i) => i.status === "taken_over").length,
    rejected: myInventoryItems.filter((i) => i.status === "rejected").length,
    completed: myInventoryItems.filter((i) => i.status === "completed").length,
  };

  const allReqByStatus = {
    draft: allRequests.filter((r) => r.status === "draft").length,
    new: allRequests.filter((r) => r.status === "new").length,
    in_progress: allRequests.filter((r) => r.status === "in_progress").length,
    completed: allRequests.filter((r) => r.status === "completed").length,
  };

  const pendingApprovalRequests = allRequests.filter(
    (r) => r.status === "draft" && r.supervisor?.id === user?.id,
  );
  const isSupervisorOfAny = allRequests.some((r) => r.supervisor?.id === user?.id);
  const showApprovalCard = isAdmin || isSupervisorOfAny || pendingApprovalRequests.length > 0;

  const allInventoryByStatus = {
    under_review: allInventoryItems.filter((i) => i.status === "under_review").length,
    taken_over: allInventoryItems.filter((i) => i.status === "taken_over").length,
    rejected: allInventoryItems.filter((i) => i.status === "rejected").length,
    completed: allInventoryItems.filter((i) => i.status === "completed").length,
  };

  const recentNotifications = notifications.slice(0, 5);

  const cardCls = classNames(styles["statsTextCenter"], isAdmin && styles["statCardCompact"]);
  const clickableCardCls = classNames(cardCls, styles["statCardClickable"]);
  const valueCls = classNames(styles["statValue"], isAdmin && styles["statValueCompact"]);
  const labelCls = classNames(styles["statLabel"], isAdmin && styles["statLabelCompact"]);

  return (
    <Page>
      <Card className={styles["section"]}>
        <div className={styles["sectionHeader"]}>
          <h2 className={styles["sectionHeading"]}>{t("nav.requests")}</h2>
          <Link to="/requests/new">
            <Button>+</Button>
          </Link>
        </div>
        {isAdmin && (
          <div className={styles["statsGrid"]}>
            <Link to="/requests" className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={valueCls}>{allRequests.length}</p>
                <p className={labelCls}>{t("dashboard.allRequests")}</p>
              </Card>
            </Link>
            <Link to={statLink("/requests", { status: "draft", locked: "1" })} className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueOrange"])}>{allReqByStatus.draft}</p>
                <p className={labelCls}>{t("dashboard.statusToApprove")}</p>
              </Card>
            </Link>
            <Link to={statLink("/requests", { status: "new", locked: "1" })} className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueBlue"])}>{allReqByStatus.new}</p>
                <p className={labelCls}>{t("dashboard.statusNew")}</p>
              </Card>
            </Link>
            <Link
              to={statLink("/requests", { status: "in_progress", locked: "1" })}
              className={styles["statCardLink"]}
            >
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueYellow"])}>{allReqByStatus.in_progress}</p>
                <p className={labelCls}>{t("dashboard.statusInProgress")}</p>
              </Card>
            </Link>
            <Link to={statLink("/requests", { status: "completed", locked: "1" })} className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueGreen"])}>{allReqByStatus.completed}</p>
                <p className={labelCls}>{t("dashboard.statusCompleted")}</p>
              </Card>
            </Link>
          </div>
        )}
        <div className={styles["statsGrid"]}>
          <Link to={statLink("/requests", { mine: "true" })} className={styles["statCardLink"]}>
            <Card className={clickableCardCls}>
              <p className={valueCls}>{myRequests.length}</p>
              <p className={labelCls}>{t("dashboard.myRequests")}</p>
            </Card>
          </Link>
          {showApprovalCard && (
            <Link to={statLink("/requests", { status: "draft", locked: "1" })} className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueOrange"])}>{pendingApprovalRequests.length}</p>
                <p className={labelCls}>{t("dashboard.statusToApprove")}</p>
              </Card>
            </Link>
          )}
          <Link
            to={statLink("/requests", { status: "new", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueBlue"])}>{myReqByStatus.new}</p>
              <p className={labelCls}>{t("dashboard.statusNew")}</p>
            </Card>
          </Link>
          <Link
            to={statLink("/requests", { status: "in_progress", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueYellow"])}>{myReqByStatus.in_progress}</p>
              <p className={labelCls}>{t("dashboard.statusInProgress")}</p>
            </Card>
          </Link>
          <Link
            to={statLink("/requests", { status: "completed", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueGreen"])}>{myReqByStatus.completed}</p>
              <p className={labelCls}>{t("dashboard.statusCompleted")}</p>
            </Card>
          </Link>
        </div>
      </Card>

      <Card className={styles["section"]}>
        <div className={styles["sectionHeader"]}>
          <h2 className={styles["sectionHeading"]}>{t("nav.inventory")}</h2>
          <Link to="/inventory/new">
            <Button>+</Button>
          </Link>
        </div>
        {isAdmin && (
          <div className={styles["statsGrid"]}>
            <Link to="/inventory" className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={valueCls}>{allInventoryItems.length}</p>
                <p className={labelCls}>{t("dashboard.allInventory")}</p>
              </Card>
            </Link>
            <Link
              to={statLink("/inventory", { status: "under_review", locked: "1" })}
              className={styles["statCardLink"]}
            >
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueBlue"])}>{allInventoryByStatus.under_review}</p>
                <p className={labelCls}>{t("inventory.statusUnderReview")}</p>
              </Card>
            </Link>
            <Link
              to={statLink("/inventory", { status: "taken_over", locked: "1" })}
              className={styles["statCardLink"]}
            >
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueYellow"])}>{allInventoryByStatus.taken_over}</p>
                <p className={labelCls}>{t("inventory.statusTakenOver")}</p>
              </Card>
            </Link>
            <Link to={statLink("/inventory", { status: "completed", locked: "1" })} className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueGreen"])}>{allInventoryByStatus.completed}</p>
                <p className={labelCls}>{t("inventory.statusCompleted")}</p>
              </Card>
            </Link>
            <Link to={statLink("/inventory", { status: "rejected", locked: "1" })} className={styles["statCardLink"]}>
              <Card className={clickableCardCls}>
                <p className={classNames(valueCls, styles["statValueRed"])}>{allInventoryByStatus.rejected}</p>
                <p className={labelCls}>{t("inventory.statusRejected")}</p>
              </Card>
            </Link>
          </div>
        )}
        <div className={styles["statsGrid"]}>
          <Link to={statLink("/inventory", { mine: "true" })} className={styles["statCardLink"]}>
            <Card className={clickableCardCls}>
              <p className={valueCls}>{myInventoryItems.length}</p>
              <p className={labelCls}>{t("dashboard.myInventory")}</p>
            </Card>
          </Link>
          <Link
            to={statLink("/inventory", { status: "under_review", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueBlue"])}>{myInventoryByStatus.under_review}</p>
              <p className={labelCls}>{t("inventory.statusUnderReview")}</p>
            </Card>
          </Link>
          <Link
            to={statLink("/inventory", { status: "taken_over", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueYellow"])}>{myInventoryByStatus.taken_over}</p>
              <p className={labelCls}>{t("inventory.statusTakenOver")}</p>
            </Card>
          </Link>
          <Link
            to={statLink("/inventory", { status: "completed", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueGreen"])}>{myInventoryByStatus.completed}</p>
              <p className={labelCls}>{t("inventory.statusCompleted")}</p>
            </Card>
          </Link>
          <Link
            to={statLink("/inventory", { status: "rejected", mine: "true", locked: "1" })}
            className={styles["statCardLink"]}
          >
            <Card className={clickableCardCls}>
              <p className={classNames(valueCls, styles["statValueRed"])}>{myInventoryByStatus.rejected}</p>
              <p className={labelCls}>{t("inventory.statusRejected")}</p>
            </Card>
          </Link>
        </div>
      </Card>

      <Card>
        <div className={styles["sectionHeader"]}>
          <h2 className={styles["sectionHeading"]}>{t("dashboard.recentNotifications")}</h2>
          <Link to="/notifications" className={styles["seeAllLink"]}>
            {t("notifications.seeAll")}
          </Link>
        </div>
        {recentNotifications.length === 0 ? (
          <p className={styles["emptyText"]}>{t("notifications.noNotifications")}</p>
        ) : (
          <ul className={styles["listSeparated"]}>
            {recentNotifications.map((n) => (
              <li key={n.id} className={classNames(styles["listSeparatedItem"], styles["listItemBlock"])}>
                <Link
                  to={n.entityType === "development_request" ? `/requests/${n.entityId}` : `/inventory/${n.entityId}`}
                  className={styles["notificationLink"]}
                  onClick={() => {
                    if (!n.readAt) markRead.mutate(n.id);
                  }}
                >
                  <span className={classNames(styles["notificationText"], !n.readAt && styles["notificationUnread"])}>
                    <strong>{formatUserName(n.actor)}</strong> {t(`notifications.actions.${n.action}`)}
                  </span>
                  <span className={styles["notificationEntity"]}>{n.entityName}</span>
                  <span className={styles["notificationTime"]}>{new Date(n.createdAt).toLocaleString()}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {isLoading && <PageLoader />}
    </Page>
  );
}
